/**
 * Entity display-name helpers.
 *
 * Entity configs carry PLURAL display names ("Deals", "Companies") because they
 * title list pages. Anything that names a single record — a create button, a
 * FAB label, an empty-state CTA — needs the singular, or the UI reads
 * "Create Deals" for an action that creates exactly one deal.
 */

/** Plural display names whose singular is not a suffix rule. */
const IRREGULAR_SINGULARS: Readonly<Record<string, string>> = {
  people: "person",
  children: "child",
  data: "record",
  media: "media",
};

/**
 * Best-effort singular of an entity display name. Handles the shapes that
 * actually occur in Hub's entity registry:
 *
 *   Deals → Deal · Companies → Company · Addresses → Address ·
 *   Time Entries → Time Entry · Communications → Communication
 *
 * Capitalization of the input is preserved. A name that is already singular
 * ("Status") or is uncountable is returned unchanged.
 */
export function singularizeEntityName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;

  // Only the LAST word is pluralized in these names ("Time Entries").
  const words = trimmed.split(/\s+/);
  const last = words[words.length - 1];
  const singularLast = singularizeWord(last);
  words[words.length - 1] = singularLast;
  return words.join(" ");
}

/**
 * Endings where a trailing "s" is part of the word, not a plural marker.
 * Without this, "Status" singularizes to "Statu".
 */
const NON_PLURAL_ENDINGS: readonly string[] = ["ss", "us", "is", "os"];

function singularizeWord(word: string): string {
  const lower = word.toLowerCase();

  const irregular = IRREGULAR_SINGULARS[lower];
  if (irregular) return matchCase(word, irregular);

  // "Companies" → "Company", "Entries" → "Entry"
  if (lower.endsWith("ies") && lower.length > 3) {
    return matchCase(word, `${lower.slice(0, -3)}y`);
  }

  // "Addresses" → "Address", "Batches" → "Batch"
  if (/(?:ss|sh|ch|x|z)es$/.test(lower)) {
    return matchCase(word, lower.slice(0, -2));
  }

  // "Deals" → "Deal". Words like "Status" / "Address" keep their final "s".
  if (
    lower.endsWith("s") &&
    lower.length > 1 &&
    !NON_PLURAL_ENDINGS.some((ending) => lower.endsWith(ending))
  ) {
    return matchCase(word, lower.slice(0, -1));
  }

  return word;
}

/**
 * Re-apply the original word's casing shape to a lowercase result, so
 * "COMPANIES" → "COMPANY" and "Companies" → "Company".
 */
function matchCase(original: string, lowerResult: string): string {
  if (original === original.toUpperCase() && original !== original.toLowerCase()) {
    return lowerResult.toUpperCase();
  }
  if (original[0] === original[0]?.toUpperCase()) {
    return lowerResult.charAt(0).toUpperCase() + lowerResult.slice(1);
  }
  return lowerResult;
}

/**
 * Label for a create action on an entity list, e.g. `New deal`. Kept in one
 * place so the FAB, the header button, and empty-state CTAs agree.
 */
export function createEntityActionLabel(pluralDisplayName: string): string {
  const singular = singularizeEntityName(pluralDisplayName);
  // Sentence case: "New time entry", not "New Time Entry" — matches the rest of
  // Hub's action buttons. Every word is lowered, not just the first, or a
  // multi-word name reads "New time Entry".
  return `New ${toSentenceCase(singular)}`;
}

/**
 * Lowercase ordinary Capitalized words while leaving acronyms and brand casing
 * intact, so "Time Entries" → "time entry" but "QuickBooks Customers" keeps its
 * "QuickBooks".
 */
function toSentenceCase(value: string): string {
  return value
    .split(/\s+/)
    .map((word) => {
      const isOrdinaryCapitalized =
        /^[A-Z][a-z]*$/.test(word) || /^[a-z]+$/.test(word);
      return isOrdinaryCapitalized ? word.toLowerCase() : word;
    })
    .join(" ");
}
